import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../redux/hooks/useAuth';
import { useSidebar } from '../redux/hooks/useSidebar';
import { usePermissions } from '../redux/hooks/usePermissions';
import { useThemeColors } from '../theme/hooks';
import { styled } from '@mui/material/styles';
import {
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import {
  Work as WorkIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
  AutoAwesome as SolutionsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Dashboard as DashboardIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import AnimatedLogo from './AnimatedLogo';

// Constants
const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 72;
const HEADER_HEIGHT = 64;

// Styled components with consistent theming
const openedMixin = (theme) => ({
  width: DRAWER_WIDTH_EXPANDED,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const StyledDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open, colors }) => ({
    width: DRAWER_WIDTH_EXPANDED,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': {
        ...openedMixin(theme),
        position: 'fixed',
        top: HEADER_HEIGHT,
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        border: 'none',
        backgroundColor: colors.gradient.background,
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
        zIndex: theme.zIndex.drawer,
      },
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': {
        ...closedMixin(theme),
        position: 'fixed',
        top: HEADER_HEIGHT,
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        border: 'none',
        backgroundColor: 'background.paper',
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
        zIndex: theme.zIndex.drawer,
      },
    }),
  }),
);

const NavigationContent = styled(Box)(({ theme, colors }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowX: 'hidden',
  background: colors.gradient.background,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.3)',
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ colors,theme, active }) => ({
  margin: theme.spacing(0.5, 1),
  minHeight: 48,
  background: active ? colors.gradient.background : 'transparent',
  color: active ? colors.gradient.primary : 'text.primary',
  '&:hover': {
    background: active ? colors.gradient.background : 'rgba(0, 0, 0, 0.04)',
  },
  '& .MuiListItemIcon-root': {
    color: active ? colors.gradient.primary : 'text.secondary',
    '& .MuiSvgIcon-root': {
      color: active ? colors.gradient.primary : 'text.secondary',
    },
  },
  transition: 'all 0.2s ease',
}));


// Contextual toggle button - background-less for subtle appearance
const ContextualToggleButton = styled(IconButton)(({ theme, colors }) => ({
  width: 24,
  height: 24,
  backgroundColor: 'transparent',
  color: colors.primary.main,
  border: 'none',
  borderRadius: '50%',
  padding: 0,
  '&:hover': {
    backgroundColor: 'rgba(124, 92, 148, 0.1)',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.2s ease',
  '& .MuiSvgIcon-root': {
    fontSize: 26,
  },
}));

const UserProfile = styled(Box)(({ theme, colors }) => ({
  // background: colors.gradient.background,
  backdropFilter: 'blur(8px)',
  padding: theme.spacing(2),
  marginTop: 'auto',
  borderBottom: `1px solid ${colors.divider}`,
}));

const UserAvatar = styled(Avatar)(({ theme, colors }) => ({
  background: colors.gradient.primary,
  fontSize: '1rem',
  fontWeight: 600,
}));

const LogoutButton = styled(IconButton)(({ theme, variant = 'expanded' }) => ({
  ...(variant === 'expanded' && {
    width: '100%',
    borderRadius: theme.spacing(1.5),
    py: 1,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: 'error.main',
    '&:hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
    },
  }),
  ...(variant === 'collapsed' && {
    width: 36,
    height: 36,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: 'error.main',
    '&:hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
    },
  }),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  padding: theme.spacing(1, 2, 0.5, 2),
  opacity: 0.8,
  display: 'block',
}));

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const colors = useThemeColors();

  const { sidebarOpen: open, toggleSidebar } = useSidebar();
  const {
    isAuthenticated,
    userRole,
    userName,
    logoutUser
  } = useAuth();

  const {
    canViewJDs,
    canViewCandidates,
    canViewAdminPanel
  } = usePermissions();

  const handleDrawerToggle = () => {
    toggleSidebar();
  };

  const handleLogout = async () => {
    await logoutUser();
  };

  // Keyboard shortcut handler for Ctrl+B
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault();
        toggleSidebar();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleSidebar]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const menuItems = [
    {
      text: 'Job Descriptions',
      icon: <WorkIcon />,
      path: '/job-descriptions',
      canAccess: canViewJDs
    },
    {
      text: 'Candidates',
      icon: <PeopleIcon />,
      path: '/candidates',
      canAccess: canViewCandidates
    },
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      canAccess: userRole === 'super_admin' || userRole === 'client_admin' || userRole === 'client_hr_manager' || userRole === 'client_interviewer' || userRole === 'client_viewer'
    },
    {
      text: 'Analytics',
      icon: <AnalyticsIcon />,
      path: '/analytics',
      canAccess: userRole === 'super_admin'
    },
    {
      text: 'Interviews',
      icon: <ScheduleIcon />,
      path: '/interviews',
      canAccess: userRole === 'super_admin' || userRole === 'client_admin' || userRole === 'client_hr_manager' || userRole === 'client_interviewer'
    },
    {
      text: 'Solutions',
      icon: <SolutionsIcon />,
      path: '/solutions',
      canAccess: true // Solutions page is accessible to all authenticated users
    },

  ];

  // Admin-only menu items
  const adminMenuItems = [
    {
      text: 'Admin Panel',
      icon: <AdminPanelSettingsIcon />,
      path: '/admin',
      canAccess: canViewAdminPanel
    },
  ];

  const renderMenuItem = (item) => {
    // Check permissions
    if (item.canAccess !== undefined && !item.canAccess) {
      return null;
    }

    const active = isActive(item.path);
    const Icon = item.icon;

    return (
      <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
        <Tooltip
          title={!open ? item.text : ''}
          placement="right"
          arrow
        >
          <StyledListItemButton
            colors={colors}
            active={active}
            onClick={() => navigate(item.path)}
            sx={{
              justifyContent: open ? 'initial' : 'center',
              px: open ? 2 : 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: active ? colors.gradient.primary : 'text.secondary',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiSvgIcon-root': {
                  color: active ? colors.gradient.primary : 'text.secondary',
                },
              }}
            >
              {Icon}
            </ListItemIcon>
            {open && (
              <ListItemText
                primary={item.text}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.9rem',
                    fontWeight: active ? 600 : 500,
                    transition: 'all 0.2s ease',
                  }
                }}
              />
            )}
          </StyledListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <StyledDrawer colors={colors} variant="permanent" open={open}>
        {/* Navigation Content */}
        <NavigationContent colors={colors}>
          {/* User Profile Section */}
          {isAuthenticated && (
            <UserProfile colors={colors}>
              {open ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0 }}>
                    <UserAvatar colors={colors}>
                      {userName?.charAt(0)?.toUpperCase() || 'U'}
                    </UserAvatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          color: 'text.primary',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {userName || 'User'}
                      </Typography>
                      <Chip
                        label={userRole?.replace('_', ' ')?.toUpperCase() || 'USER'}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          height: 20,
                          mt: 0.5
                        }}
                      />
                    </Box>

                    {/* Toggle Button - positioned to the right of user info in expanded mode */}
                    <Tooltip
                      title="Collapse sidebar"
                      arrow
                      placement="top"
                    >
                      <ContextualToggleButton colors={colors} onClick={handleDrawerToggle}>
                        <ChevronLeftIcon />
                      </ContextualToggleButton>
                    </Tooltip>
                  </Box>

                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  {/* Toggle Button - positioned above profile in collapsed mode */}
               
                  <ContextualToggleButton colors={colors} onClick={handleDrawerToggle}>
                  <Tooltip title={`${userName || 'User'} (${userRole?.toUpperCase() || 'USER'})`} arrow placement="right">
                    <UserAvatar colors={colors} sx={{ width: 36, height: 36, fontSize: '0.9rem' }}>
                      {userName?.charAt(0)?.toUpperCase() || 'U'}
                    </UserAvatar>
                  </Tooltip>
                  </ContextualToggleButton>

                </Box>
              )}
            </UserProfile>
          )}
          {/* Main Navigation Content */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 2 }}>
            {/* Main Navigation */}
            <List sx={{ px: 0 }}>
              {menuItems.map(renderMenuItem)}
            </List>

            {/* Admin Section */}
            {canViewAdminPanel && (
              <>
                <Divider sx={{ my: 2, mx: 2, opacity: 0.3 }} />
                {open && <SectionTitle>Administration</SectionTitle>}
                <List sx={{ px: 0 }}>
                  {adminMenuItems.map(renderMenuItem)}
                </List>
              </>
            )}
          </Box>

          {/* User Profile Section */}
          {isAuthenticated && (
            <Box sx={{ my: 2 }}>
              {open ? (
                <Tooltip title="Logout" arrow>
                  <LogoutButton variant="expanded" onClick={handleLogout}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Logout
                    </Typography>
                  </LogoutButton>
                </Tooltip>

              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="Logout" arrow placement="right">
                    <LogoutButton variant="collapsed" onClick={handleLogout}>
                      <LogoutIcon fontSize="small" />
                    </LogoutButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
        </NavigationContent>
      </StyledDrawer>
    </Box>
  );
};

export default Sidebar; 